import React, { useMemo, useState } from 'react';
import { cn } from '../lib/utils';
import {
  AlertTriangle, Clock, Flame, UserPlus, CalendarClock, ChevronRight,
  Phone, Mail, MessageSquare, CheckCircle2, Loader2, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import GradientAvatar from './GradientAvatar';

/**
 * LeadActionListView
 * A ranked, single-list "smart inbox" of what the broker needs to do next.
 * No columns to manage — purely focus-driven.
 *
 * Buckets, in priority order:
 *   1. Overdue follow-ups (red)
 *   2. Due Today follow-ups (amber)
 *   3. Hot leads with no pending follow-up (orange)
 *   4. New leads with no first contact yet (cyan)
 *   5. Upcoming this week (slate)
 *
 * Each row exposes one-tap actions (open lead, mark done with outcome).
 */

const BUCKETS = [
  { key: 'overdue',       label: 'Overdue',                accent: 'rose',    icon: AlertTriangle, hint: 'Clear these first' },
  { key: 'due_today',     label: 'Due Today',              accent: 'amber',   icon: Clock,         hint: 'On your plate today' },
  { key: 'hot_no_action', label: 'Hot · No scheduled contact', accent: 'orange', icon: Flame,      hint: 'Strike while warm' },
  { key: 'no_contact',    label: 'No first contact',       accent: 'cyan',    icon: UserPlus,      hint: 'Make the first call' },
  { key: 'upcoming_week', label: 'Upcoming this week',     accent: 'slate',   icon: CalendarClock, hint: 'On the horizon' },
];

const ACCENT_CLASSES = {
  rose:   { dot: 'bg-rose-500',   text: 'text-rose-700 dark:text-rose-300',   strip: 'bg-rose-500',   pill: 'bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300',   ring: 'ring-rose-500/30' },
  amber:  { dot: 'bg-amber-500',  text: 'text-amber-700 dark:text-amber-300', strip: 'bg-amber-500',  pill: 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300', ring: 'ring-amber-500/30' },
  orange: { dot: 'bg-orange-500', text: 'text-orange-700 dark:text-orange-300',strip: 'bg-orange-500', pill: 'bg-orange-100 text-orange-700 dark:bg-orange-500/15 dark:text-orange-300', ring: 'ring-orange-500/30' },
  cyan:   { dot: 'bg-cyan-500',   text: 'text-cyan-700 dark:text-cyan-300',   strip: 'bg-cyan-500',   pill: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-500/15 dark:text-cyan-300', ring: 'ring-cyan-500/30' },
  slate:  { dot: 'bg-slate-400',  text: 'text-slate-700 dark:text-slate-300', strip: 'bg-slate-400',  pill: 'bg-slate-100 text-slate-700 dark:bg-slate-700/40 dark:text-slate-300', ring: 'ring-slate-500/20' },
};

const PRIORITY_PILL = {
  Hot:  'bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300',
  Warm: 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300',
  Cold: 'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300',
};

const METHOD_ICON = { call: Phone, sms: MessageSquare, email: Mail };

const isOverdue = (iso) => {
  if (!iso) return false;
  return Date.parse(iso) < Date.now();
};
const isToday = (iso) => {
  if (!iso) return false;
  const t = new Date(iso);
  const now = new Date();
  return t.getFullYear() === now.getFullYear() && t.getMonth() === now.getMonth() && t.getDate() === now.getDate();
};
const isThisWeek = (iso) => {
  if (!iso) return false;
  const t = Date.parse(iso);
  const inSeven = Date.now() + 7 * 24 * 60 * 60 * 1000;
  return t > Date.now() && t < inSeven;
};
const formatWhen = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  const now = new Date();
  const diffMs = d.getTime() - now.getTime();
  const diffMin = Math.round(diffMs / 60000);
  const abs = Math.abs(diffMin);
  if (abs < 60) return diffMin >= 0 ? `in ${abs}m` : `${abs}m ago`;
  if (abs < 24 * 60) return diffMin >= 0 ? `in ${Math.round(abs / 60)}h` : `${Math.round(abs / 60)}h ago`;
  return d.toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
};

const LeadActionListView = ({
  leads = [],
  followUps = { overdue: [], today: [], week: [] },
  onSelectLead,
  onCompleteFollowUp,         // (followUp, outcome) => Promise
  loading = false,
}) => {
  const [busyId, setBusyId] = useState(null);
  const leadById = useMemo(() => {
    const m = {};
    leads.forEach((l) => { m[l.id] = l; });
    return m;
  }, [leads]);

  // Build buckets
  const buckets = useMemo(() => {
    const all = [...(followUps.overdue || []), ...(followUps.today || []), ...(followUps.week || [])];
    const seenFollowUpLeadIds = new Set(all.map((fu) => fu.lead_id));

    const overdueRows = (followUps.overdue || []).filter((fu) => fu.status !== 'completed' && isOverdue(fu.scheduled_at));
    const todayRows = (followUps.today || []).filter((fu) => fu.status !== 'completed' && isToday(fu.scheduled_at) && !isOverdue(fu.scheduled_at));
    const weekRows = (followUps.week || []).filter((fu) => fu.status !== 'completed' && isThisWeek(fu.scheduled_at));

    const isInFlight = (l) => !['converted', 'lost', 'long_term_nurture'].includes(l.status);
    const hotNoAction = leads.filter((l) => l.priority === 'Hot' && isInFlight(l) && !seenFollowUpLeadIds.has(l.id));
    const noContact = leads.filter((l) => l.status === 'new_lead' && !l.first_contacted_at && !seenFollowUpLeadIds.has(l.id));

    return {
      overdue: overdueRows,
      due_today: todayRows,
      hot_no_action: hotNoAction,
      no_contact: noContact,
      upcoming_week: weekRows,
    };
  }, [leads, followUps]);

  const totalActions = Object.values(buckets).reduce((sum, arr) => sum + arr.length, 0);

  const handleOutcome = async (fu, outcome) => {
    setBusyId(fu.id);
    try {
      await onCompleteFollowUp(fu, outcome);
    } catch (e) {
      toast.error('Could not complete follow-up');
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 text-emerald-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="lead-action-list-view">
      {/* Summary strip */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5">
        {BUCKETS.map(({ key, label, accent, icon: Icon }) => {
          const accentCls = ACCENT_CLASSES[accent];
          const count = buckets[key].length;
          return (
            <div
              key={key}
              className={cn(
                'rounded-xl px-3 py-2.5 border bg-white dark:bg-slate-900/60 border-slate-200 dark:border-slate-800 flex items-center gap-2.5',
                count === 0 && 'opacity-60'
              )}
              data-testid={`action-summary-${key}`}
            >
              <span className={cn('w-7 h-7 rounded-md flex items-center justify-center', accentCls.pill)}>
                <Icon className="w-3.5 h-3.5" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400 truncate">{label}</p>
                <p className={cn('text-xl font-bold tabular-nums leading-tight', accentCls.text)}>{count}</p>
              </div>
            </div>
          );
        })}
      </div>

      {totalActions === 0 && (
        <div className="rounded-2xl border border-dashed border-emerald-300/60 dark:border-emerald-500/30 bg-emerald-50/40 dark:bg-emerald-500/5 p-10 text-center" data-testid="action-list-empty">
          <Sparkles className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
          <h3 className="text-lg font-bold text-emerald-700 dark:text-emerald-300">All clear.</h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Nothing overdue, nothing due today, no untouched hot leads. Enjoy the calm.
          </p>
        </div>
      )}

      {/* Buckets */}
      {BUCKETS.map(({ key, label, accent, icon: Icon, hint }) => {
        const rows = buckets[key];
        if (rows.length === 0) return null;
        const accentCls = ACCENT_CLASSES[accent];

        return (
          <section key={key} className="space-y-2" data-testid={`action-bucket-${key}`}>
            <div className="flex items-center gap-2.5">
              <span className={cn('flex items-center justify-center w-7 h-7 rounded-md', accentCls.pill)}>
                <Icon className="w-4 h-4" />
              </span>
              <div className="flex-1 min-w-0">
                <h3 className={cn('text-sm sm:text-base font-bold leading-tight', accentCls.text)}>
                  {label}
                  <span className="ml-2 text-xs font-semibold tabular-nums opacity-70">({rows.length})</span>
                </h3>
                <p className="text-[11px] text-slate-500 dark:text-slate-400">{hint}</p>
              </div>
            </div>

            <ul className="space-y-1.5">
              {rows.map((item) => {
                // Two shapes: a follow-up row (has lead_id + scheduled_at) OR a lead row.
                const isFollowUp = Boolean(item.scheduled_at && item.lead_id);
                const lead = isFollowUp ? leadById[item.lead_id] : item;
                if (!lead) return null;
                const MethodIcon = isFollowUp ? (METHOD_ICON[item.method] || Phone) : null;
                const busy = busyId === item.id;
                const priorityPill = PRIORITY_PILL[lead.priority] || PRIORITY_PILL.Warm;
                const noAns = lead.no_answer_attempts || 0;

                return (
                  <li
                    key={isFollowUp ? item.id : `lead-${lead.id}-${key}`}
                    onClick={() => onSelectLead && onSelectLead(lead)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelectLead && onSelectLead(lead); } }}
                    className={cn(
                      'group relative flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700/80 cursor-pointer transition-all',
                      'hover:border-emerald-400 dark:hover:border-emerald-500 hover:shadow-md hover:-translate-y-0.5'
                    )}
                    data-testid={`action-row-${lead.id}-${key}`}
                  >
                    {/* Accent edge strip */}
                    <span className={cn('absolute left-0 top-2 bottom-2 w-1 rounded-r', accentCls.strip)} aria-hidden="true" />

                    {/* Gradient avatar */}
                    <GradientAvatar
                      name={lead.name}
                      role="Lead"
                      keyId={lead.id || lead.name}
                      size="sm"
                      className="ml-2 w-9 h-9 text-[10px]"
                      subtitle={lead.status?.replace(/_/g, ' ')}
                    />

                    <div className="pl-1 flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-semibold text-slate-900 dark:text-white truncate max-w-[16rem]">{lead.name}</p>
                        <span className={cn('px-1.5 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wide', priorityPill)}>{lead.priority || 'Warm'}</span>
                        {noAns > 0 && lead.status === 'new_lead' && (
                          <span className={cn(
                            'px-1.5 py-0.5 rounded-full text-[10px] font-medium tabular-nums',
                            noAns >= 2 ? 'bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300' : 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300'
                          )}>
                            {noAns}/3 no-answer
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate mt-0.5">
                        {isFollowUp ? (
                          <span className="inline-flex items-center gap-1">
                            <MethodIcon className="w-3 h-3" />
                            {item.method?.toUpperCase()}
                            <span className="opacity-50"> · {formatWhen(item.scheduled_at)}</span>
                            {item.notes && <span className="opacity-70 truncate"> · {item.notes}</span>}
                          </span>
                        ) : (
                          <span>
                            {lead.lead_type || '—'}
                            {lead.lead_source ? <span className="opacity-50"> · {lead.lead_source}</span> : null}
                          </span>
                        )}
                      </p>
                    </div>

                    {/* Quick actions */}
                    <div className="flex items-center gap-1.5 shrink-0">
                      {isFollowUp && (
                        <select
                          defaultValue=""
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => {
                            const outcome = e.target.value;
                            if (!outcome) return;
                            handleOutcome(item, outcome === 'done' ? '' : outcome);
                            e.target.value = '';
                          }}
                          disabled={busy}
                          className="text-xs h-8 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-1.5 disabled:opacity-50"
                          data-testid={`action-outcome-${item.id}`}
                          aria-label="Mark outcome"
                        >
                          <option value="">Mark…</option>
                          <option value="done">✓ Done · Spoke</option>
                          <option value="no_answer">No answer</option>
                          <option value="voicemail">Voicemail</option>
                          <option value="no_response">No response</option>
                          <option value="scheduled">Scheduled next</option>
                        </select>
                      )}
                      {busy ? (
                        <Loader2 className="w-4 h-4 animate-spin text-emerald-500" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-emerald-500 group-hover:translate-x-0.5 transition-all" />
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
    </div>
  );
};

export default LeadActionListView;
